import {useMemo} from "react";
import {
	DEFAULT_STRENGTH_RESULT_OPTIONS,
	DEFAULT_STRENGTH_RULES,
	determinePasswordStrength,
	type StrengthResultOption,
	type StrengthRule,
} from "@/features/auth/passwordStrength";
import type {StrengthLevel} from "@/primitives/PasswordStrengthMeter";

export interface PasswordStrength {
	/** Named level (`"very-weak"` … `"very-strong"`), or `null` while the field is empty. */
	level: StrengthLevel | null;
	/** Reached level as a number (`0` when empty), ready to hand to `PasswordStrengthMeter.value`. */
	order: number;
	/** Highest reachable level — `PasswordStrengthMeter.max`. */
	maxOrder: number;
	/** Convenience `order / maxOrder` as a 0–100 percentage. */
	percent: number;
	/** Human-readable label for the current level. */
	label: string;
}

export interface UsePasswordStrengthOptions {
	rules?: StrengthRule[];
	resultOptions?: StrengthResultOption[];
	/** Override the label per level name; falls back to the built-in English labels. */
	labels?: Partial<Record<StrengthLevel, string>>;
	/** Label shown while the field is empty. */
	emptyLabel?: string;
}

const DEFAULT_LABELS: Record<StrengthLevel, string> = {
	"very-weak": "Very weak",
	weak: "Weak",
	medium: "Medium",
	strong: "Strong",
	"very-strong": "Very strong",
};

/**
 * React adapter over the pure `determinePasswordStrength` scorer. Turns a password
 * string into the props `PasswordStrengthMeter` wants, and — unlike the raw scorer,
 * which rates `""` as very-weak — reports an empty field as a neutral, unrated state
 * so the meter starts blank.
 */
export function usePasswordStrength(password: string, options: UsePasswordStrengthOptions = {}): PasswordStrength {
	const {
		rules = DEFAULT_STRENGTH_RULES,
		resultOptions = DEFAULT_STRENGTH_RESULT_OPTIONS,
		labels,
		emptyLabel = "Enter a password",
	} = options;

	return useMemo(() => {
		const maxOrder = resultOptions.reduce((max, option) => Math.max(max, option.order), 0);

		if (!password) {
			return {level: null, order: 0, maxOrder, percent: 0, label: emptyLabel};
		}

		const result = determinePasswordStrength(password, rules, resultOptions);
		const order = result?.order ?? 0;
		const level = result?.name ?? null;
		const percent = maxOrder > 0 ? Math.round((order / maxOrder) * 100) : 0;
		const label = (level && (labels?.[level] ?? DEFAULT_LABELS[level])) ?? emptyLabel;

		return {level, order, maxOrder, percent, label};
	}, [password, rules, resultOptions, labels, emptyLabel]);
}
